import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FluxiLogo from "../assets/svg/FluxiLogo.svg";
import { menuItems } from "../const/menuItems";
import { SidebarItem } from "./SidebarItem";
import { dictionary } from "../utils/dictionary";
import { useAuth } from "../context/AuthContext";
import { Chip } from "./common/chip/Chip";
import { ModalWrapper } from "./modals/ModalWrapper";
import { BuyCreditsModal } from "./modals/BuyCreditsModal";
import { membershipStatsAxios } from "../services/membership/MembershipAxios";
import { handleApi } from "../utils/handleApi";

const Sidebar = ({ isTutorial }: { isTutorial: boolean }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [indicatorOffset, setIndicatorOffset] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isOpenBuyCreditsModal, setIsOpenBuyCreditsModal] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  const [endMembershipDate, setEndMembershipDate] = useState("");
  const [loadingCredits, setLoadingCredits] = useState(false);
  const path = location.pathname;
  const { language } = useAuth();

  const handleBuyCredits = () => {
    setIsOpenBuyCreditsModal(true);
  };

  const closeModal = () => {
    setIsOpenBuyCreditsModal(false);
  };

  useEffect(() => {
    setLoadingCredits(true);
    const getStats = async () => {
      const { res } = await handleApi(membershipStatsAxios());
      if (res) {
        if (!res.active_membership) {
          navigate("/plans");
          return;
        }
        setUserCredits(res.active_membership.credits);
        setEndMembershipDate(res.active_membership.ends_at);
      }
      setLoadingCredits(false);
    };
    getStats();
  }, []);

  return (
    <>
      {isOpenBuyCreditsModal && (
        <ModalWrapper isOpen={isOpenBuyCreditsModal} onClose={closeModal}>
          <BuyCreditsModal
            onClose={closeModal}
            nextRenewalDate={endMembershipDate}
            language={language}
          />
        </ModalWrapper>
      )}
      <div
        className="w-60 h-screen bg-[#FFFFFF] border-r border-gray-200 flex flex-col fixed left-0 top-0"
        style={{ zIndex: 2 }}
      >
        <div
          className="p-4 cursor-pointer"
          onClick={() =>
            isTutorial ? () => { } : navigate("/home")
          }
        >
          <img src={FluxiLogo} alt="Fluxi Logo" className="pl-3 h-auto" />
        </div>

        <nav className="sidebar-nav flex-1 overflow-y-auto">
          <div className="flex flex-col justify-between h-full">
            {/* Active indicator */}
            <div className="relative">
              <div
                className="absolute left-0 w-1 h-12 bg-[#EBEEFF] rounded-r-xl transition-all duration-200"
                style={{
                  transform: `translateY(${indicatorOffset}px)`,
                  opacity: indicatorOffset >= 0 ? 1 : 0,
                }}
              />

              {/* Main menu items */}
              <div className="menu-section px-4 space-y-1">
                {menuItems
                  .filter((item) => item.section === "main")
                  .map((item) => (
                    <SidebarItem
                      key={item.path}
                      icon={item.icon}
                      text={dictionary[item.text]?.[language] || item.text}
                      path={item.path}
                      active={location.pathname === item.path}
                      disabled={
                        (location.pathname === "/plans" || isTutorial) ? true : item.disabled
                      }
                      onClick={item.onClick}
                    />
                  ))}
              </div>

              {/* Settings section */}
              <div className="menu-section px-4 space-y-1">
                <div className="mx-4 border-t border-[#EAEAEA] mt-2"></div>
                {menuItems
                  .filter((item) => item.section === "settings")
                  .map((item) => (
                    <SidebarItem
                      key={item.path}
                      icon={item.icon}
                      text={dictionary[item.text]?.[language] || item.text}
                      path={item.path}
                      active={location.pathname === item.path}
                      disabled={isTutorial ? true : item.disabled}
                      onClick={() => navigate(item.path)}
                    />
                  ))}
              </div>
            </div>

            <div className="relative px-4 mt-auto">
              <div
                className={`
            absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-white rounded-lg shadow-lg
            transition-all duration-200 ease-in-out max-w-[90%] mx-auto
            ${isHovered
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-2 pointer-events-none"
                  }
          `}
              >
                <div className="relative">
                  <p className="text-sm font-medium text-gray-800 text-center break-words">
                    {dictionary["come-ready"][language]}
                  </p>
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-white" />
                </div>
              </div>

              {path !== "/home" && (
                <img
                  src="https://fluxi.co/prod/assets/svgs/fluxi-pet-2.svg"
                  alt="Fluxi Mascot"
                  className={`w-full transition-opacity duration-200 ease-in-out ${isHovered ? "opacity-90" : "opacity-[0.69]"
                    }`}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                />
              )}
              {path == "/home" && (
                <Chip
                  text={dictionary["credits"][language]}
                  value={userCredits.toString()}
                  onClick={handleBuyCredits}
                  loading={loadingCredits}
                />
              )}
            </div>
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
